import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import zhConvertor from 'zhconvertor';
import SeatDemographics from '../SeatDetail/SeatDemographics';
import { SeatResultBody } from '../SeatDetail/SeatsDetail';
import '../SeatDetail/SeatsDetail.css';
import './SeatOnly.css';

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const PREFS_KEY = 'seatonly_prefs';
const DEFAULT_PREFS = {
  showElectorate: true,
  showEnglishName: false,
  showLast: false,
};

// Full-viewport broadcast view of one seat. Clicking anywhere closes it, so
// nothing inside may swallow clicks (SeatResultBody runs interactive=false).
const BroadcastOverlay = ({ seat, lastSeat, prefs, onClose }) => {
  const showPast = prefs.showLast && lastSeat;
  return (
    <div className="seatonly_overlay" onClick={onClose}>
      <div className="seatonly_overlay_inner">
        <div className="seatonly_overlay_header">
          <div className="seatonly_overlay_seatname">{seat.seatname}</div>
          <div className="seatonly_overlay_edname">
            {seat.ednamec} <span>{seat.edname}</span>
          </div>
        </div>
        {prefs.showElectorate && seat.showinfo === true && (
          <SeatDemographics
            regPercent={seat.reg_percent}
            electorate={seat.electorate}
          />
        )}
        <div className={`seatonly_results${showPast ? ' has_past' : ''}`}>
          {window.hideCurrentContestant === false && (
            <div className="seatonly_result_col">
              <SeatResultBody
                seat={seat}
                interactive={false}
                showEnglishName={prefs.showEnglishName}
              />
            </div>
          )}
          {showPast && (
            <div className="seatonly_result_col">
              <h4 className="seat_last_title">{tr('上届成绩')}</h4>
              <SeatResultBody
                seat={lastSeat}
                interactive={false}
                showEnglishName={prefs.showEnglishName}
              />
            </div>
          )}
        </div>
        <div className="seatonly_overlay_url">2026 森美兰州选 
          https://sembilan2026.orientaldaily.com.my
        </div>
      </div>
    </div>
  );
};

const SeatOnly = () => {
  const [data, setData] = useState([]);
  const [lastData, setLastData] = useState(null);
  const [activeSeatName, setActiveSeatName] = useState(null);
  const [prefs, setPrefs] = useState(() => {
    try {
      return {
        ...DEFAULT_PREFS,
        ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'),
      };
    } catch {
      return DEFAULT_PREFS;
    }
  });

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    let cancelled = false;
    const fetchSeats = () => {
      const lang = localStorage.getItem('lang') || 'zh_CN';
      axios
        .get(`public-api/seatinfo?seat=negerisembilan&code=N&lang=${lang}`)
        .then((res) => {
          if (!cancelled) setData(res.data || []);
        })
        .catch(() => {});
    };
    fetchSeats();
    const interval = setInterval(fetchSeats, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const lang = localStorage.getItem('lang') || 'zh_CN';
    axios
      .get(`public-api/seatinfo?seat=negerisembilan&code=N&last_election=true&lang=${lang}`)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data || []).forEach((s) => {
          map[s.seatname] = s;
        });
        setLastData(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const seats = useMemo(
    () =>
      [...data].sort(
        (a, b) =>
          parseInt(a.seatname.slice(1)) - parseInt(b.seatname.slice(1))
      ),
    [data]
  );

  // Derived (not snapshotted) so the 15s poll keeps an open popup live.
  const activeSeat = activeSeatName
    ? seats.find((s) => s.seatname === activeSeatName)
    : null;

  useEffect(() => {
    if (!activeSeatName) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setActiveSeatName(null);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [activeSeatName]);

  const togglePref = (key) =>
    setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="seatonly_page">
      <Helmet>
        <title>Seat Broadcast</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="seatonly_prefs">
        <label>
          <input
            type="checkbox"
            checked={prefs.showElectorate}
            onChange={() => togglePref('showElectorate')}
          />
          {tr('显示选民人数')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={prefs.showEnglishName}
            onChange={() => togglePref('showEnglishName')}
          />
          {tr('显示候选人英文名')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={prefs.showLast}
            onChange={() => togglePref('showLast')}
          />
          {tr('显示上届成绩')}
        </label>
      </div>
      <div className="seatonly_grid">
        {seats.map((seat) => (
          <button
            key={seat.seatname}
            type="button"
            className="seatonly_item"
            onClick={() => setActiveSeatName(seat.seatname)}
          >
            <span className="seatonly_item_no">{seat.seatname}</span>
            <span className="seatonly_item_name">{seat.ednamec}</span>
            <span className="seatonly_item_ename">{seat.edname}</span>
          </button>
        ))}
      </div>
      {activeSeat && (
        <BroadcastOverlay
          seat={activeSeat}
          lastSeat={lastData ? lastData[activeSeat.seatname] : null}
          prefs={prefs}
          onClose={() => setActiveSeatName(null)}
        />
      )}
    </div>
  );
};

export default SeatOnly;
